/*
 * Pre-order checkout gap rule.
 *
 * Blocks checkout (theme-level, soft block) when the cart contains two or more
 * pre-order items whose release dates (product.metafields.custom.in_stock_date) are more
 * than the merchant-configured number of months apart. The customer is shown a clear
 * notice naming the pre-orders that break the rule.
 *
 * Data comes from the #PreorderCartRuleData JSON rendered by
 * sections/preorder-cart-rule.liquid. After every cart change we re-fetch that section
 * via the Section Rendering API because /cart.js does not expose product metafields.
 *
 * Enforcement covers the theme's own checkout entry points (cart page, cart drawer and
 * add-to-cart notification) plus the cart-level express/dynamic checkout buttons. It
 * cannot stop a directly-typed /checkout URL — that requires a Shopify checkout
 * validation Function.
 */
(function () {
  'use strict';

  var SECTION_ID = 'preorder-cart-rule';
  var DATA_SELECTOR = '#PreorderCartRuleData';
  var NOTICE_SELECTOR = '[data-preorder-cart-notice]';
  var CHECKOUT_SELECTORS = [
    '#checkout',
    '#CartDrawer-Checkout',
    '#cart-notification-form button[name="checkout"]',
  ];
  var BLOCKED_ATTR = 'data-preorder-blocked';

  var lastResult = { blocked: false };

  function parseData(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[preorder-cart-rule] could not parse data', e);
      return null;
    }
  }

  function parseISO(value) {
    if (typeof value !== 'string' || !value) return null;
    var parts = value.split('-');
    if (parts.length !== 3) return null;
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return new Date(year, month - 1, day);
  }

  // Add whole calendar months, clamping to the last valid day of the target month
  // (e.g. Jan 31 + 1 month => Feb 28/29).
  function addMonths(date, months) {
    var result = new Date(date.getTime());
    var targetDay = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    var daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(targetDay, daysInTargetMonth));
    return result;
  }

  function formatDisplayDate(date) {
    try {
      return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (e) {
      return date.toISOString().slice(0, 10);
    }
  }

  function evaluate(data) {
    var threshold = data && typeof data.thresholdMonths === 'number' && data.thresholdMonths >= 1 ? data.thresholdMonths : 3;
    var strings = (data && data.strings) || {};

    var dated = ((data && data.items) || [])
      .map(function (item) {
        return { title: item.title, url: item.url, key: item.key, dateObj: parseISO(item.date) };
      })
      .filter(function (item) {
        return item.dateObj instanceof Date && !isNaN(item.dateObj.getTime());
      })
      .sort(function (a, b) {
        return a.dateObj - b.dateObj;
      });

    if (dated.length < 2) {
      return { blocked: false, threshold: threshold, strings: strings };
    }

    var earliest = dated[0];
    var latest = dated[dated.length - 1];
    var blocked = addMonths(earliest.dateObj, threshold) < latest.dateObj;

    return {
      blocked: blocked,
      threshold: threshold,
      strings: strings,
      items: dated,
      earliest: earliest,
      latest: latest,
    };
  }

  function buildNoticeHtml(result) {
    var s = result.strings || {};
    var months = String(result.threshold);

    var title = s.title || 'Pre-order dates are too far apart';
    var body = (s.body || 'These pre-orders ship more than [months] months apart. Please place them as separate orders.').replace('[months]', months);
    var itemTpl = s.item || '[title] — ships [date]';

    var listItems = result.items
      .map(function (item) {
        var isExtreme = item === result.earliest || item === result.latest;
        var line = itemTpl
          .replace('[title]', escapeHtml(item.title))
          .replace('[date]', escapeHtml(formatDisplayDate(item.dateObj)));
        var url = item.url ? '<a class="preorder-cart-notice__link" href="' + escapeAttr(item.url) + '">' + line + '</a>' : line;
        return '<li class="preorder-cart-notice__item' + (isExtreme ? ' preorder-cart-notice__item--flagged' : '') + '">' + url + '</li>';
      })
      .join('');

    // title/body/separate/item come from Liquid's {{ ... | t }}, which already
    // HTML-escapes them. Re-escaping here would double-encode characters such as
    // apostrophes (' -> &#39; -> &amp;#39;, rendered literally as "&#39;"), so we
    // insert them as-is. Only raw values interpolated in JS (product titles, dates)
    // are escaped, above.
    var separate = s.separate ? '<p class="preorder-cart-notice__hint">' + s.separate + '</p>' : '';

    return (
      '<div class="preorder-cart-notice" role="alert">' +
      '<p class="preorder-cart-notice__title">' + title + '</p>' +
      '<p class="preorder-cart-notice__body">' + body + '</p>' +
      '<ul class="preorder-cart-notice__list">' + listItems + '</ul>' +
      separate +
      '</div>'
    );
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }

  function toggleCheckoutButton(button, blocked) {
    if (!button) return;
    if (blocked) {
      if (!button.disabled) {
        button.setAttribute('data-preorder-disabled', 'true');
      }
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    } else if (button.getAttribute('data-preorder-disabled') === 'true') {
      // Only re-enable buttons that we disabled (leave empty-cart disabling intact).
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-preorder-disabled');
    }
  }

  function applyState(result) {
    lastResult = result;
    var blocked = !!result.blocked;

    document.querySelectorAll(NOTICE_SELECTOR).forEach(function (container) {
      container.innerHTML = blocked ? buildNoticeHtml(result) : '';
      container.hidden = !blocked;
    });

    CHECKOUT_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (button) {
        toggleCheckoutButton(button, blocked);
      });
    });

    if (blocked) {
      document.body.setAttribute(BLOCKED_ATTR, 'true');
    } else {
      document.body.removeAttribute(BLOCKED_ATTR);
    }
  }

  function refreshFromServer() {
    var base = (window.routes && window.routes.cart_url) || '/';
    var separator = base.indexOf('?') === -1 ? '?' : '&';
    var url = base + separator + 'section_id=' + SECTION_ID;

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector(DATA_SELECTOR);
        var current = document.querySelector(DATA_SELECTOR);
        if (fresh && current) {
          current.textContent = fresh.textContent;
        }
        applyState(evaluate(parseData(document.querySelector(DATA_SELECTOR))));
      })
      .catch(function (e) {
        // eslint-disable-next-line no-console
        console.warn('[preorder-cart-rule] refresh failed', e);
      });
  }

  function observeNotification() {
    // The add-to-cart notification opens by toggling classes on #cart-notification and
    // re-renders only its product/button children (not our notice or checkout button).
    // Watch just the host's class so we can re-apply the current blocked state when it
    // opens. Observing only attributes (not the subtree) avoids an infinite loop, since
    // applyState mutates descendants of this element.
    var notification = document.getElementById('cart-notification');
    if (!notification || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function () {
      applyState(lastResult);
    });

    observer.observe(notification, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function init() {
    applyState(evaluate(parseData(document.querySelector(DATA_SELECTOR))));
    observeNotification();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        // Cart sections were just re-rendered, resetting our button/notice state on the
        // fresh nodes. Re-apply the previous result immediately to avoid a flash, then
        // refresh from the server for the accurate (post-change) state.
        applyState(lastResult);
        refreshFromServer();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
